import React, { useMemo, useState, useEffect, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Users,
  ArrowRight,
  Globe2,
  MessageSquare,
  CalendarPlus,
  CheckCircle2,
  CreditCard,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { recordInterests } from "@/lib/interests";
import { useAuth } from "@/context/AuthContext";
import { getEventById, bookEvent } from "@/Services/EventServices";
import { getSessionTables } from "@/Services/EventMeetingServices";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — module-scope
// ---------------------------------------------------------------------------

const fmtRange = (startDateStr, endDateStr) => {
  try {
    if (!startDateStr || !endDateStr) return "";
    const s = new Date(startDateStr);
    const e = new Date(endDateStr);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "";
    const sameYear = s.getFullYear() === e.getFullYear();
    const sameDay = s.toDateString() === e.toDateString();
    const md = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    });
    const y = new Intl.DateTimeFormat(undefined, { year: "numeric" });
    if (sameDay) return `${md.format(s)}, ${y.format(s)}`;
    if (sameYear) return `${md.format(s)} – ${md.format(e)}, ${y.format(e)}`;
    const mdy = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${mdy.format(s)} – ${mdy.format(e)}`;
  } catch {
    return "";
  }
};

function liveLabel(start, end) {
  const now = Date.now();
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e))
    return { text: "TBA", tone: "future" };
  if (now < s) {
    const diff = s - now;
    const days = Math.floor(diff / 86400000);
    if (days >= 1) return { text: `Starts in ${days}d`, tone: "future" };
    return { text: "Starts soon", tone: "future" };
  }
  if (now < e) return { text: "Live now", tone: "live" };
  return { text: "Ended", tone: "past" };
}

// ---------------------------------------------------------------------------
// MODAL SHELL — shared backdrop + panel primitive
// ---------------------------------------------------------------------------

const ModalShell = memo(({ onClose, testId, children }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="w-[min(96vw,520px)] rounded-3xl border border-white/15 bg-[#0A1D1A] p-7 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
));
ModalShell.displayName = "ModalShell";

// ---------------------------------------------------------------------------
// REGISTER MODAL
// ---------------------------------------------------------------------------

const RegisterModal = memo(({ event, open, onClose, user, onBooked }) => {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset checkbox every time the modal opens
  useEffect(() => {
    if (open) setAgree(false);
  }, [open]);

  const submit = useCallback(async () => {
    if (!agree) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }
    if (!user || user === false) {
      toast.error("Please sign in to register.");
      return;
    }
    setBusy(true);
    try {
      const res = await bookEvent({
        newsId: event.id,
        agreeTerms: true,
        userName: user?.name,
        firstName: user?.name?.split(" ")?.[0],
        lastName: user?.name?.split(" ")?.slice(1)?.join(" "),
        email: user?.email,
      });

      const ticketNumber = res?.booking?.ticketNumber || "";
      if (!ticketNumber && !/booked/i.test(String(res?.message || ""))) {
        toast.error(res?.message || "Could not register.");
        return;
      }
      toast.success(res?.message || "Event booked successfully");
      onClose?.();
      await onBooked?.(ticketNumber);
    } catch {
      toast.error("Could not register.");
    } finally {
      setBusy(false);
    }
  }, [agree, user, event, onClose, onBooked]);

  if (!open) return null;

  return (
    <ModalShell onClose={onClose} testId="event-register-modal">
      <p className="overline mb-2">Register</p>
      <h3 className="font-display text-2xl tracking-tight mb-2">
        {event?.title}
      </h3>

      <label className="flex items-start gap-3 mt-4 cursor-pointer">
        <input
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className="mt-1"
        />
        <span className="text-sm text-white/70">
          I agree to the{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>

      <div className="flex gap-3 mt-6">
        <button
          onClick={submit}
          className="btn-gold flex-1 justify-center"
          data-testid="event-register-confirm"
          type="button"
          disabled={!agree || busy}
        >
          {busy ? "Registering…" : "Confirm registration"}{" "}
          <ArrowRight size={16} />
        </button>
        <button
          onClick={onClose}
          className="btn-ghost"
          type="button"
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
});
RegisterModal.displayName = "RegisterModal";

// ---------------------------------------------------------------------------
// CANCEL REGISTRATION MODAL
// ---------------------------------------------------------------------------

const CancelRegistrationModal = memo(
  ({ open, onClose, eventId, onCancelled }) => {
    const [busy, setBusy] = useState(false);

    const submit = useCallback(async () => {
      setBusy(true);
      try {
        const res = await bookEvent({ newsId: eventId, cancel: true });
        if (
          res?.removed === true ||
          /cancel/i.test(String(res?.message || ""))
        ) {
          toast.success(res?.message || "Booking cancelled successfully");
          onClose?.();
          await onCancelled?.();
          return;
        }
        toast.error(res?.message || "Could not cancel registration.");
      } catch {
        toast.error("Could not cancel registration.");
      } finally {
        setBusy(false);
      }
    }, [eventId, onClose, onCancelled]);

    if (!open) return null;

    return (
      <ModalShell onClose={onClose} testId="cancel-register-modal">
        <p className="overline mb-2">Cancel registration</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">
          Are you sure?
        </h3>
        <p className="text-sm text-white/65 mb-6">
          Your booking will be removed.
        </p>
        <div className="flex gap-3">
          <button
            onClick={submit}
            className="btn-gold flex-1 justify-center"
            type="button"
            disabled={busy}
          >
            {busy ? "Canceling…" : "Yes, cancel"} <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="btn-ghost"
            type="button"
            disabled={busy}
          >
            Keep my seat
          </button>
        </div>
      </ModalShell>
    );
  },
);
CancelRegistrationModal.displayName = "CancelRegistrationModal";

// ---------------------------------------------------------------------------
// MAIN EVENT CARD COMPONENT
// ---------------------------------------------------------------------------

export default function EventCard({ event, sponsors = [] }) {
  const { user } = useAuth();

  // Modal state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Lazy-loaded data
  const [fullEvent, setFullEvent] = useState(null);
  const [tableCount, setTableCount] = useState(0);
  const [loadingExtras, setLoadingExtras] = useState(true);

  // Local registration override — set after successful register/cancel
  // so the card updates immediately without waiting for a full refetch
  const [localRegState, setLocalRegState] = useState(null); // null | true | false

  // ---------------------------------------------------------------------------
  // Fetch full event details + table counts
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let alive = true;

    async function loadData() {
      try {
        const res = await getEventById(event.id);
        if (!alive || !res?.success) return;

        const detailedData = res.data;
        setFullEvent(detailedData);

        const sessions = detailedData?.sessions || [];
        if (sessions.length > 0) {
          const tableRes = await Promise.all(
            sessions.map((s) => getSessionTables(s._id).catch(() => null)),
          );
          const total = tableRes.reduce(
            (acc, r) => acc + (r?.data?.tables?.length || 0),
            0,
          );
          if (alive) setTableCount(total);
        }
      } catch (err) {
        console.error("Card data fetch error:", err);
      } finally {
        if (alive) setLoadingExtras(false);
      }
    }

    loadData();
    return () => {
      alive = false;
    };
  }, [event.id]);

  // ---------------------------------------------------------------------------
  // Derive registration status from server data (or local override)
  // ---------------------------------------------------------------------------
  const isUserRegistered = useMemo(() => {
    // Local override takes precedence (set after register/cancel actions)
    if (localRegState !== null) return localRegState;

    const data = fullEvent || event?.raw || event;
    if (data?.isBooked) return true;

    const myId = user?._id || user?.id;
    if (!myId || !data?.bookingDetails) return false;

    return data.bookingDetails.some((b) => {
      const uid = b?.userId?._id || b?.userId;
      return uid && String(uid) === String(myId);
    });
  }, [localRegState, fullEvent, event, user]);

  // ---------------------------------------------------------------------------
  // Stable handlers
  // ---------------------------------------------------------------------------
  const handleRegisterOpen = useCallback(() => setRegisterOpen(true), []);
  const handleRegisterClose = useCallback(() => setRegisterOpen(false), []);
  const handleCancelOpen = useCallback(() => setCancelOpen(true), []);
  const handleCancelClose = useCallback(() => setCancelOpen(false), []);

  const handleBooked = useCallback(async (_ticketNumber) => {
    // Optimistically mark as registered; background refetch will confirm
    setLocalRegState(true);
  }, []);

  const handleCancelled = useCallback(async () => {
    // Optimistically mark as not registered
    setLocalRegState(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------------
  const tags = useMemo(
    () => (Array.isArray(event?.tags) ? event.tags : []),
    [event],
  );
  const live = liveLabel(event?.start_time, event?.end_time);
  const price = Number(event?.price ?? 0);
  const dateRangeText =
    fmtRange(event?.startDate, event?.endDate) ||
    fmtRange(event?.start_time, event?.end_time);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <article className="glass rounded-3xl overflow-hidden group transition-all duration-300 ease-out hover:-translate-y-1 hover:border-white/20">
      {/* Cover image */}
      <Link
        to={`/events/${event.id}`}
        className="block aspect-[5/3] overflow-hidden relative"
        onClick={() => recordInterests(tags)}
      >
        <img
          src={event?.cover || event?.eventImage || ""}
          alt=""
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061412] via-[#061412]/30 to-transparent" />
        <div className="absolute top-5 left-5 flex gap-2">
          <span className="overline inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/30 text-white bg-[#061412]/70 backdrop-blur-sm">
            <Globe2 size={10} /> {event?.meetingType || "Virtual"}
          </span>
          <span
            className={`overline inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#061412]/70 backdrop-blur-sm border ${
              live.tone === "live"
                ? "border-[#2A7E5D]/60 text-[#7BC8A6]"
                : "border-[#C6A87C]/60 text-[#C6A87C]"
            }`}
          >
            {live.tone === "live" && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] ring-live" />
            )}
            {live.text}
          </span>
        </div>
      </Link>

      {/* Body */}
      <div className="p-8 md:p-9">
        <p className="overline mb-3 text-[#C6A87C]">{tags.join(" · ")}</p>
        <Link to={`/events/${event.id}`}>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-4 leading-[1.05] hover:text-[#C6A87C] transition-colors">
            {event.title}
          </h3>
        </Link>
        <div
          className="text-white/65 text-sm md:text-base leading-relaxed mb-7 line-clamp-3"
          dangerouslySetInnerHTML={{
            __html: event.description || "",
          }}
        />

        {/* Meta grid */}
        <div className="grid grid-cols-3 gap-4 text-sm text-white/75 border-t border-white/[0.06] pt-6">
          <div className="flex items-start gap-2">
            <Calendar size={16} className="mt-0.5 text-[#C6A87C] shrink-0" />
            <div>
              <p className="text-white/95 font-medium">{dateRangeText}</p>
              <p className="text-white/65">
                {String(event?.timezone || "UTC").replace(/[()]/g, "")}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Users size={16} className="mt-0.5 text-[#C6A87C] shrink-0" />
            <div>
              <p className="text-white/95 font-medium">
                {event?.registered || 0}
              </p>
              <p className="text-white/65">registered</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MessageSquare
              size={16}
              className="mt-0.5 text-[#C6A87C] shrink-0"
            />
            <div>
              <p className="text-white/95 font-medium">
                {loadingExtras ? "…" : tableCount}
              </p>
              <p className="text-white/65">tables running</p>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-7 flex items-center justify-between gap-3 flex-wrap">
          {isUserRegistered ? (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Registered pill */}
              <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#7BC8A6]/15 border border-[#7BC8A6]/45 text-[#7BC8A6] text-sm">
                <CheckCircle2 size={15} /> You're in
              </span>

              {/* Cancel button */}
              <button
                type="button"
                onClick={handleCancelOpen}
                className="text-xs text-white/50 hover:text-red-400 transition-colors underline underline-offset-2"
                data-testid={`event-card-cancel-${event.id}`}
              >
                Cancel registration
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleRegisterOpen}
              className="text-sm flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93] transition-colors font-semibold"
              data-testid={`event-card-register-${event.id}`}
            >
              {price > 0
                ? `Register — $${Math.round(price)}`
                : "Register to attend"}{" "}
              <ArrowRight size={14} />
            </button>
          )}

          <div className="flex items-center gap-4">
            <Link
              to={`/events/${event.id}`}
              className="text-xs text-white/60 hover:text-[#C6A87C] flex items-center gap-1"
            >
              See agenda <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>

      {/* Modals — only mount DOM when open */}
      <RegisterModal
        open={registerOpen}
        onClose={handleRegisterClose}
        event={event}
        user={user}
        onBooked={handleBooked}
      />

      <CancelRegistrationModal
        open={cancelOpen}
        onClose={handleCancelClose}
        eventId={event.id}
        onCancelled={handleCancelled}
      />
    </article>
  );
}